import PropTypes from "prop-types";
import * as React from "react";
import { bulkProductAction } from "../../../services/bulkProduct";
import { Button, Typography } from "../../../themeV2";
import MessageModal from "../../MessageModal";
import css from "./BulkActionsToolbar.module.css";
import BulkChangeProductTypeDialog from "./BulkChangeProductTypeDialog";
import BulkDeleteDialog from "./BulkDeleteDialog";
import BulkDuplicateDialog from "./BulkDuplicateDialog";
import BulkSignersDialog from "./BulkSignersDialog";
import BulkTagsDialog from "./BulkTagsDialog";

const BulkActionsToolbar = React.memo(function BulkActionsToolbar({
  isAdmin,
  selectedIds,
  clearSelection,
  maxSelection,
  products,
  refresh,
}) {
  const [confirmDialog, setConfirmDialog] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [showTagsDialog, setShowTagsDialog] = React.useState(false);
  const [showSignersDialog, setShowSignersDialog] = React.useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = React.useState(false);
  const [showChangeTypeDialog, setShowChangeTypeDialog] = React.useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [confirmError, setConfirmError] = React.useState(null);

  const sharedProductTypeId = React.useMemo(() => {
    if (selectedIds.size === 0 || !products?.length) return undefined;
    const selectedProducts = products.filter((p) =>
      selectedIds.has(p.productId),
    );
    if (selectedProducts.length === 0) return undefined;
    const firstTypeId = selectedProducts[0].productTypeId;
    return selectedProducts.every((p) => p.productTypeId === firstTypeId)
      ? firstTypeId
      : undefined;
  }, [selectedIds, products]);

  const count = selectedIds.size;

  const handleActionComplete = React.useCallback(() => {
    setConfirmDialog(null);
    setConfirmError(null);
    setShowTagsDialog(false);
    setShowSignersDialog(false);
    setShowDuplicateDialog(false);
    setShowChangeTypeDialog(false);
    setShowDeleteDialog(false);
    clearSelection();
    refresh();
  }, [clearSelection, refresh]);

  const handleConfirmAction = React.useCallback(async () => {
    if (!confirmDialog) return;

    setIsLoading(true);
    setConfirmError(null);
    try {
      await bulkProductAction({
        productIds: [...selectedIds],
        action: confirmDialog.action,
      });
      handleActionComplete();
    } catch (err) {
      setConfirmError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [confirmDialog, selectedIds, handleActionComplete]);

  return (
    <>
      <div className={css.toolbar}>
        <div className={css.selectionInfo}>
          <Typography variant="body-lg" as="span">
            <strong>{count}</strong> / {maxSelection} selected
          </Typography>
          <Button variant="text" size="sm" onClick={clearSelection}>
            Clear
          </Button>
        </div>

        <div className={css.actions}>
          <Button
            variant="tonal"
            size="sm"
            onClick={() => setShowTagsDialog(true)}
          >
            Add Tags
          </Button>

          {isAdmin && (
            <Button
              variant="tonal"
              size="sm"
              onClick={() => setShowSignersDialog(true)}
            >
              Add Signers
            </Button>
          )}

          <Button
            variant="tonal"
            size="sm"
            onClick={() =>
              setConfirmDialog({
                action: "hide",
                title: `Deactivate ${count} Product${count !== 1 ? "s" : ""}?`,
                description: `This will deactivate ${count} product${
                  count !== 1 ? "s" : ""
                } from the shop.`,
                confirmLabel: "Deactivate",
              })
            }
          >
            Deactivate
          </Button>

          <Button
            variant="tonal"
            size="sm"
            onClick={() =>
              setConfirmDialog({
                action: "unhide",
                title: `Activate ${count} Product${count !== 1 ? "s" : ""}?`,
                description: `This will activate ${count} product${
                  count !== 1 ? "s" : ""
                } on the shop.`,
                confirmLabel: "Activate",
              })
            }
          >
            Activate
          </Button>

          <Button
            variant="tonal"
            size="sm"
            onClick={() => setShowDuplicateDialog(true)}
          >
            Duplicate as...
          </Button>

          {isAdmin && sharedProductTypeId !== undefined && (
            <Button
              variant="tonal"
              size="sm"
              onClick={() => setShowChangeTypeDialog(true)}
            >
              Change Type
            </Button>
          )}

          {isAdmin && (
            <Button
              className={css.deleteAction}
              variant="outlined"
              color="error"
              size="sm"
              onClick={() => setShowDeleteDialog(true)}
            >
              Delete
            </Button>
          )}
        </div>
      </div>

      {confirmDialog && (
        <MessageModal
          open
          type="info"
          title={confirmDialog.title}
          message={
            <>
              {confirmDialog.description}
              {confirmError && (
                <Typography
                  variant="body-lg"
                  as="p"
                  style={{
                    color: "var(--md-error)",
                    marginBlockStart: "var(--space-4)",
                  }}
                >
                  {confirmError}
                </Typography>
              )}
            </>
          }
          buttonText="Cancel"
          confirmText={confirmDialog.confirmLabel}
          onClose={() => {
            setConfirmDialog(null);
            setConfirmError(null);
          }}
          onConfirm={handleConfirmAction}
          buttonProps={{ disabled: isLoading }}
          confirmButtonProps={{ loading: isLoading }}
        />
      )}

      <BulkDuplicateDialog
        open={showDuplicateDialog}
        onClose={() => setShowDuplicateDialog(false)}
        selectedIds={selectedIds}
        onActionComplete={handleActionComplete}
      />

      <BulkChangeProductTypeDialog
        open={showChangeTypeDialog}
        onClose={() => setShowChangeTypeDialog(false)}
        selectedIds={selectedIds}
        currentProductTypeId={sharedProductTypeId}
        onActionComplete={handleActionComplete}
      />

      <BulkDeleteDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        selectedIds={selectedIds}
        onActionComplete={handleActionComplete}
      />

      <BulkSignersDialog
        open={showSignersDialog}
        onClose={() => setShowSignersDialog(false)}
        selectedIds={selectedIds}
        onActionComplete={handleActionComplete}
      />

      <BulkTagsDialog
        isAdmin={isAdmin}
        open={showTagsDialog}
        onClose={() => setShowTagsDialog(false)}
        selectedIds={selectedIds}
        onActionComplete={handleActionComplete}
      />
    </>
  );
});

BulkActionsToolbar.displayName = "BulkActionsToolbar";

BulkActionsToolbar.propTypes = {
  clearSelection: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  maxSelection: PropTypes.number.isRequired,
  products: PropTypes.array.isRequired,
  refresh: PropTypes.func.isRequired,
  selectedIds: PropTypes.instanceOf(Set).isRequired,
};

export default BulkActionsToolbar;
